import { listLogsQuerySchema, type ListLogsQuery } from "@woven/contracts";
import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { EmptyState, ErrorState, LoadingState } from "../../components/StatusView";
import { StatusIndicator } from "../../components/ui/StatusIndicator";
import { useLogs } from "../../features/logs/logHooks";
import { useServers } from "../../features/servers/serverHooks";

export function ConsoleLogsPage() {
  const [params, setParams] = useSearchParams();
  const raw = Object.fromEntries(
    Array.from(new Set(params.keys()), (key) => {
      const values = params.getAll(key);
      return [key, values.length === 1 ? values[0] : values];
    })
  );
  const parsed = listLogsQuerySchema.safeParse(raw);
  if (!parsed.success) {
    return (
      <div className="console-page">
        <h1 className="console-page-title">Logs</h1>
        <ErrorState
          title="Invalid log filters"
          message="Choose a supported server and level. Legacy resource filters must be a single global value."
          onRetry={() => setParams({})}
        />
      </div>
    );
  }
  if (parsed.data.resourceId) {
    const next = new URLSearchParams(params);
    next.delete("resourceId");
    next.delete("cursor");
    return <Navigate to={{ search: next.size ? `?${next}` : "" }} replace />;
  }
  return <LogsView query={parsed.data} />;
}

function LogsView({ query }: { query: ListLogsQuery }) {
  const [params, setParams] = useSearchParams();
  const [live, setLive] = useState(true);
  const { state, reload } = useLogs(query, live);
  const servers = useServers();
  const availableServers = servers.state.status === "ready" ? servers.state.data.items : [];
  const selectedServerMissing =
    query.serverId && !availableServers.some((server) => server.id === query.serverId);

  function filter(key: "serverId" | "level", value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("cursor");

    setParams(next);
  }

  function page(cursor: string | null) {
    const next = new URLSearchParams(params);
    if (cursor) next.set("cursor", cursor);
    else next.delete("cursor");
    setParams(next);
  }

  return (
    <div className="console-page">
      <div className="dashboard-preview-head">
        <div className="console-page-head">
          <h1 className="console-page-title">Logs</h1>
          <p className="console-page-sub">
            Client messages, connections and server events. Managed servers retain the latest 200
            events.
          </p>
        </div>
        <div className="dashboard-preview-head-actions">
          <label className="logs-live-toggle">
            <input
              type="checkbox"
              checked={live && !query.cursor}
              disabled={Boolean(query.cursor)}
              onChange={(event) => setLive(event.target.checked)}
            />
            Live Updates
          </label>
          <button type="button" className="btn btn--ghost btn--sm" onClick={reload}>
            Refresh Logs
          </button>
        </div>
      </div>

      <section className="panel logs-filters" aria-label="Log filters">
        <label className="field">
          <span className="field-label">Server</span>
          <select
            className="input"
            value={query.serverId ?? ""}
            disabled={servers.state.status !== "ready"}
            onChange={(event) => filter("serverId", event.target.value)}
          >
            <option value="">All servers</option>
            {selectedServerMissing && (
              <option value={query.serverId}>Selected server ({query.serverId})</option>
            )}
            {availableServers.map((server) => (
              <option key={server.id} value={server.id}>
                {server.name} ({server.id})
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Level</span>
          <select
            className="input"
            value={query.level ?? ""}
            onChange={(event) => filter("level", event.target.value)}
          >
            <option value="">All levels</option>
            <option value="info">Info</option>
            <option value="warn">Warning</option>
            <option value="error">Error</option>
          </select>
        </label>
      </section>

      {servers.state.status === "error" && (
        <div role="status">
          Server filter choices could not be loaded. Your selected log scope is unchanged.{" "}
          <button type="button" className="btn btn--ghost btn--sm" onClick={servers.reload}>
            Retry Server List
          </button>
        </div>
      )}

      {state.status === "loading" ? (
        <LoadingState label="Loading logs…" />
      ) : state.status === "error" ? (
        <ErrorState title="Unable to load logs" message={state.message} onRetry={reload} />
      ) : state.data.items.length === 0 ? (
        <EmptyState
          title="No events in this scope"
          body="Server registrations, client messages and connection events appear here. Earlier events are not backfilled."
        />
      ) : (
        <div className="panel logs-table-wrap">
          <table className="data-table logs-table">
            <caption className="visually-hidden">Logs for your servers</caption>
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">Level</th>
                <th scope="col">Resource</th>
                <th scope="col">Server</th>
                <th scope="col">Event</th>
              </tr>
            </thead>
            <tbody>
              {state.data.items.map((entry) => (
                <tr key={entry.id}>
                  <td className="cell-mono">
                    <time dateTime={entry.occurredAt}>
                      {new Date(entry.occurredAt).toLocaleString()}
                    </time>
                  </td>
                  <td>
                    <StatusIndicator
                      tone={
                        entry.level === "error"
                          ? "danger"
                          : entry.level === "warn"
                            ? "warn"
                            : "info"
                      }
                      label={
                        entry.level === "warn"
                          ? "Warning"
                          : entry.level === "error"
                            ? "Error"
                            : "Info"
                      }
                    />
                  </td>
                  <td>Global Fabric</td>
                  <td>
                    <Link to={`/console/servers/${encodeURIComponent(entry.serverId)}`}>
                      {entry.serverName} ({entry.serverId})
                    </Link>
                  </td>
                  <td>
                    <span className="cell-mono">{entry.event}</span>
                    <p className="cell-mono">
                      {entry.source === "client"
                        ? "Client"
                        : entry.source === "node"
                          ? "Node"
                          : "Host"}
                      {entry.connectionId && ` · Connection ${entry.connectionId}`}
                      {entry.namespaceId && ` · Namespace ${entry.namespaceId}`}
                      {entry.sessionId && ` · Session ${entry.sessionId}`}
                    </p>
                    <p className="logs-message">{entry.message}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="logs-pagination">
        <span className="console-page-sub">
          {state.status === "ready" ? `${state.data.items.length} events` : "—"} · newest first · up
          to {query.limit ?? 50} per page
        </span>
        <div className="dashboard-preview-head-actions">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={!query.cursor}
            onClick={() => page(null)}
          >
            Latest
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={state.status !== "ready" || !state.data.nextCursor}
            onClick={() => {
              if (state.status === "ready") page(state.data.nextCursor);
            }}
          >
            Older
          </button>
        </div>
      </div>
    </div>
  );
}
